import { useAuth, useUser } from "@clerk/react"
import { useAppDispatch, useAppSelector } from "hooks/storeHooks"
import queryClient from "providers/queryClient"
import React, { useEffect, useState } from "react"
import { billingApi } from "services/billingApi"
import { identifyPlatformUser, resetPlatformAnalytics } from "services/platformAnalytics"
import { tintVTOService } from "services/tintvto"
import { clearBillingState } from "store/billing/billingSlice"
import { clearProductSlice, setTypes } from "store/product/productSlice"
import { clearUserInfo, selectUserInfo, setUserInfo } from "store/user/userSlice"
import { initializeUser } from "store/user/userThunks"
import { IntegrationType, UserStatusTypes } from "types"
import { clearTiendanubeToken } from "utils/authSessionStorage"
import { logger } from "utils/logger"
import { normalizeMerchantStatus } from "utils/userStatus"
import { TintMark } from "@/components/TintMark/TintMark"

type ClerkWindow = Window & {
  Clerk?: {
    signOut?: (options?: { redirectUrl?: string }) => Promise<void>
  }
}

type ClerkUserLike = {
  fullName?: string | null
  firstName?: string | null
  lastName?: string | null
  username?: string | null
  primaryEmailAddress?: {
    emailAddress?: string | null
  } | null
  emailAddresses?: Array<{ emailAddress?: string | null }>
} | null

const AUTH_RESOLUTION_REASON = "auth-resolution"
const MERCHANT_RESOLUTION_MAX_ATTEMPTS = 12
const MERCHANT_RESOLUTION_RETRY_DELAY_MS = 3000

type ProvisioningState = {
  isVisible: boolean
  progress: number
  stepLabel: string
}

const INITIAL_PROVISIONING_STATE: ProvisioningState = {
  isVisible: false,
  progress: 0,
  stepLabel: "",
}

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const tokenParts = token.split(".")
    if (tokenParts.length < 2) return null

    const normalizedPayload = tokenParts[1].replace(/-/g, "+").replace(/_/g, "/")
    const paddedPayload = normalizedPayload.padEnd(Math.ceil(normalizedPayload.length / 4) * 4, "=")

    return JSON.parse(window.atob(paddedPayload))
  } catch {
    return null
  }
}

const toPlatformUsername = (sub: string): string => {
  const normalized = sub.startsWith("user_") ? sub.slice(5) : sub
  return normalized.endsWith("-PLATFORM") ? normalized : `${normalized}-PLATFORM`
}

const toAppGroup = (group: string): string => {
  const normalized = group.trim().toLowerCase()
  if (normalized === "admin") return "Admin"
  if (normalized === "merchant") return "Merchant"
  if (normalized === "editor") return "Editor"
  return group.trim()
}

const normalizeGroups = (payload: Record<string, unknown> | null): string[] => {
  const source = payload?.groups ?? payload?.["cognito:groups"]
  const groupsFromClaims = Array.isArray(source)
    ? source.filter((group): group is string => typeof group === "string")
    : typeof source === "string"
    ? [source]
    : []

  const groups = groupsFromClaims
    .map((group) => group.trim())
    .filter(Boolean)
    .map(toAppGroup)

  return [...new Set(groups)]
}

const ensureAdminGroup = (groups: string[]): string[] => {
  if (groups.includes("Admin")) return groups

  return [...groups, "Admin"]
}

const normalizeDisplayName = (value: unknown): string => {
  if (typeof value !== "string") return ""
  return value.trim()
}

const formatEmailToDisplayName = (email: string): string => {
  const normalizedEmail = email.trim()
  if (!normalizedEmail) return ""

  const [localPart] = normalizedEmail.split("@")
  if (!localPart) return normalizedEmail

  const words = localPart
    .split(/[._-]+/)
    .map((chunk) => chunk.trim())
    .filter(Boolean)

  if (!words.length) return normalizedEmail

  return words.map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`).join(" ")
}

const resolveDisplayNameFromClaims = (payload: Record<string, unknown> | null): string => {
  const fullName = normalizeDisplayName(payload?.name)
  if (fullName) return fullName

  const givenName = normalizeDisplayName(payload?.given_name)
  const familyName = normalizeDisplayName(payload?.family_name)
  const combined = [givenName, familyName].filter(Boolean).join(" ").trim()
  if (combined) return combined

  const preferredUsername = normalizeDisplayName(payload?.preferred_username)
  if (preferredUsername) return preferredUsername

  const username = normalizeDisplayName(payload?.username)
  if (username) return username

  const email = normalizeDisplayName(payload?.email)
  if (email) return formatEmailToDisplayName(email)

  return ""
}

const resolveDisplayNameFromClerkUser = (user: ClerkUserLike): string => {
  if (!user) return ""

  const fullName = normalizeDisplayName(user.fullName)
  if (fullName) return fullName

  const givenName = normalizeDisplayName(user.firstName)
  const familyName = normalizeDisplayName(user.lastName)
  const combined = [givenName, familyName].filter(Boolean).join(" ").trim()
  if (combined) return combined

  const username = normalizeDisplayName(user.username)
  if (username) return username

  const primaryEmail = normalizeDisplayName(user.primaryEmailAddress?.emailAddress)
  if (primaryEmail) return formatEmailToDisplayName(primaryEmail)

  const fallbackEmail = normalizeDisplayName(user.emailAddresses?.[0]?.emailAddress)
  if (fallbackEmail) return formatEmailToDisplayName(fallbackEmail)

  return ""
}

const sanitizeIdentity = (value: string): string => {
  if (!value) return ""
  if (value.length <= 6) return `${value.slice(0, 2)}***`
  return `${value.slice(0, 4)}...${value.slice(-4)}`
}

const getErrorType = (error: unknown): string => {
  if (error instanceof Error) return error.name
  if (typeof error === "object" && error) {
    if ("name" in error && typeof error.name === "string") return error.name
    if ("statusCode" in error && typeof error.statusCode === "number")
      return `http_${error.statusCode}`
  }

  return "unknown_error"
}

const areStringArraysEqual = (first: string[], second: string[]) => {
  if (first.length !== second.length) return false
  return first.every((value, index) => value === second[index])
}

const Initializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch()
  const { isLoaded, isSignedIn } = useAuth()
  const { isLoaded: isClerkUserLoaded, user: clerkUser } = useUser()
  const userInfo = useAppSelector(selectUserInfo)
  const [isInitializing, setIsInitializing] = useState(true)
  const [, setProvisioningState] = useState<ProvisioningState>(INITIAL_PROVISIONING_STATE)
  const clerkDisplayName = resolveDisplayNameFromClerkUser(clerkUser ?? null)

  useEffect(() => {
    void dispatch(initializeUser())
  }, [dispatch])

  useEffect(() => {
    let isCanceled = false

    const resetAuthState = () => {
      clearTiendanubeToken()
      resetPlatformAnalytics()
      dispatch(clearBillingState())
      dispatch(clearUserInfo())
      dispatch(clearProductSlice())
      queryClient.clear()
      tintVTOService.changeAwsTokenAndUserType(null, IntegrationType.COMMON)
      setProvisioningState(INITIAL_PROVISIONING_STATE)
    }

    const delay = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms))

    const blockAuthResolution = async (event: string, context: Record<string, unknown>) => {
      logger.error("auth", event, {
        reason: AUTH_RESOLUTION_REASON,
        ...context,
      })

      resetAuthState()
      if (isCanceled) return

      setIsInitializing(false)

      const redirectPath = `/login?reason=${AUTH_RESOLUTION_REASON}`
      const redirectUrl = `${window.location.origin}${redirectPath}`
      const clerk = (window as ClerkWindow).Clerk

      if (!clerk?.signOut) {
        window.location.replace(redirectPath)
        return
      }

      try {
        await clerk.signOut({ redirectUrl })
        return
      } catch (error) {
        logger.error("auth", "merchant_auth_block_signout_failed", {
          reason: AUTH_RESOLUTION_REASON,
          errorType: getErrorType(error),
        })
      }

      window.location.replace(redirectPath)
    }

    const waitForMerchantId = async (
      username: string,
      context: { sub: string; username: string }
    ): Promise<string | null> => {
      for (let attempt = 1; attempt <= MERCHANT_RESOLUTION_MAX_ATTEMPTS; attempt++) {
        if (isCanceled) return null

        const progress = Math.min(
          82,
          15 + Math.round((attempt / MERCHANT_RESOLUTION_MAX_ATTEMPTS) * 67)
        )
        setProvisioningState({
          isVisible: true,
          progress,
          stepLabel: `Preparing your workspace (${attempt}/${MERCHANT_RESOLUTION_MAX_ATTEMPTS})`,
        })

        try {
          const { data } = await tintVTOService.resolveMerchantByUserName(username)
          const merchantId = data?.resolveMerchantByUName ?? ""
          if (merchantId) return merchantId
        } catch (error) {
          logger.warn("auth", "merchant_resolve_retry_attempt_failed", {
            ...context,
            errorType: getErrorType(error),
            attempt,
            maxAttempts: MERCHANT_RESOLUTION_MAX_ATTEMPTS,
          })
        }

        if (attempt < MERCHANT_RESOLUTION_MAX_ATTEMPTS)
          await delay(MERCHANT_RESOLUTION_RETRY_DELAY_MS)
      }

      return null
    }

    const waitForMerchantStatus = async (
      merchantId: string,
      context: { sub: string; username: string }
    ): Promise<UserStatusTypes | null> => {
      for (let attempt = 1; attempt <= MERCHANT_RESOLUTION_MAX_ATTEMPTS; attempt++) {
        if (isCanceled) return null

        const progress = Math.min(
          96,
          82 + Math.round((attempt / MERCHANT_RESOLUTION_MAX_ATTEMPTS) * 14)
        )
        setProvisioningState({
          isVisible: true,
          progress,
          stepLabel: `Finalizing account setup (${attempt}/${MERCHANT_RESOLUTION_MAX_ATTEMPTS})`,
        })

        try {
          const { data } = await tintVTOService.merchantStatus(merchantId)
          const rawMerchantStatus = data?.merchantStatus
          if (typeof rawMerchantStatus === "string" && rawMerchantStatus.trim())
            return normalizeMerchantStatus(rawMerchantStatus)
        } catch (error) {
          logger.warn("auth", "merchant_status_retry_attempt_failed", {
            ...context,
            merchantId: sanitizeIdentity(merchantId),
            errorType: getErrorType(error),
            attempt,
            maxAttempts: MERCHANT_RESOLUTION_MAX_ATTEMPTS,
          })
        }

        if (attempt < MERCHANT_RESOLUTION_MAX_ATTEMPTS)
          await delay(MERCHANT_RESOLUTION_RETRY_DELAY_MS)
      }

      return null
    }

    const syncClerkAuth = async () => {
      if (userInfo.integrationType === IntegrationType.TIENDANUBE && userInfo.username) {
        setProvisioningState(INITIAL_PROVISIONING_STATE)
        setIsInitializing(false)
        return
      }

      if (!isLoaded || (isSignedIn && !isClerkUserLoaded)) return

      if (!isSignedIn) {
        if (userInfo.integrationType === IntegrationType.COMMON && userInfo.username)
          resetAuthState()

        setProvisioningState(INITIAL_PROVISIONING_STATE)
        setIsInitializing(false)
        return
      }

      const token = await tintVTOService.getClerkAppsyncToken()
      const payload = token ? decodeJwtPayload(token) : null
      const rawSub = typeof payload?.sub === "string" ? payload.sub : ""
      if (!rawSub) {
        await blockAuthResolution("jwt_template_missing_sub", {
          claimKeys: payload ? Object.keys(payload) : [],
        })
        return
      }

      const username = toPlatformUsername(rawSub)
      let groups = normalizeGroups(payload)
      const displayName = clerkDisplayName || resolveDisplayNameFromClaims(payload) || username
      let adminBypassPromise: Promise<boolean> | null = null
      const hasPlatformAdminBypass = async () => {
        if (!adminBypassPromise)
          adminBypassPromise = billingApi
            .stripeEntitlement({ suppressPaywall: true })
            .then((entitlement) => entitlement.entitlementStatus === "admin_bypass")
            .catch(() => false)

        return adminBypassPromise
      }

      if (groups.length === 0)
        if (await hasPlatformAdminBypass()) groups = ensureAdminGroup(groups)
        else
          logger.warn("auth", "jwt_template_missing_groups_merchant_fallback", {
            claimKeys: payload ? Object.keys(payload) : [],
            sub: sanitizeIdentity(rawSub),
            aud: payload?.aud,
            iss: payload?.iss,
          })

      let isAdmin = groups.includes("Admin")

      let merchantId = ""
      let status = UserStatusTypes.ACTIVE
      if (!isAdmin) {
        const sanitizedContext = {
          sub: sanitizeIdentity(rawSub),
          username: sanitizeIdentity(username),
        }

        merchantId = (await waitForMerchantId(username, sanitizedContext)) ?? ""
        if (!merchantId)
          if (await hasPlatformAdminBypass()) {
            groups = ensureAdminGroup(groups)
            isAdmin = true
          } else {
            await blockAuthResolution("merchant_resolve_by_username_failed", {
              ...sanitizedContext,
              errorType: "not_resolved_after_retries",
            })
            return
          }

        if (!isAdmin) {
          const resolvedStatus = await waitForMerchantStatus(merchantId, sanitizedContext)
          if (!resolvedStatus) {
            await blockAuthResolution("merchant_status_lookup_failed", {
              ...sanitizedContext,
              merchantId: sanitizeIdentity(merchantId),
              errorType: "not_resolved_after_retries",
            })
            return
          }

          status = resolvedStatus
        }
      }

      if (isCanceled) return

      const nextUserInfo = {
        integrationType: IntegrationType.COMMON,
        username,
        displayName,
        groups,
        merchantId,
        status,
        userExpired: false,
      }

      const isSameUserInfo =
        userInfo.integrationType === nextUserInfo.integrationType &&
        userInfo.username === nextUserInfo.username &&
        userInfo.displayName === nextUserInfo.displayName &&
        userInfo.merchantId === nextUserInfo.merchantId &&
        userInfo.status === nextUserInfo.status &&
        userInfo.userExpired === nextUserInfo.userExpired &&
        areStringArraysEqual(userInfo.groups, nextUserInfo.groups)

      if (!isSameUserInfo) dispatch(setUserInfo({ userInfo: nextUserInfo }))
      identifyPlatformUser(nextUserInfo)
      tintVTOService.changeAwsTokenAndUserType(null, IntegrationType.COMMON)
      setProvisioningState({
        isVisible: false,
        progress: 100,
        stepLabel: "",
      })
      setIsInitializing(false)
    }

    void syncClerkAuth()

    return () => {
      isCanceled = true
    }
  }, [
    dispatch,
    clerkDisplayName,
    isClerkUserLoaded,
    isLoaded,
    isSignedIn,
    userInfo.displayName,
    userInfo.groups,
    userInfo.integrationType,
    userInfo.merchantId,
    userInfo.status,
    userInfo.userExpired,
    userInfo.username,
  ])

  useEffect(() => {
    if (isInitializing || !userInfo.username) return

    identifyPlatformUser(userInfo)
  }, [isInitializing, userInfo])

  useEffect(() => {
    if (isInitializing || !userInfo.username) return

    let isCanceled = false

    const loadTypes = async () => {
      try {
        const { data } = await tintVTOService.types()
        if (isCanceled) return

        dispatch(setTypes(data?.types ?? []))
      } catch (error) {
        if (isCanceled) return

        console.warn("Failed to preload product types", error)
        dispatch(setTypes([]))
      }
    }

    void loadTypes()

    return () => {
      isCanceled = true
    }
  }, [dispatch, isInitializing, userInfo.username])

  if (isInitializing)
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <TintMark className="size-12 animate-pulse rounded-xl" />
      </div>
    )

  return <>{children}</>
}

export default Initializer
